import { useEffect, useState, useRef } from "react";

// Material UI
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Popper from "@mui/material/Popper";

// FontAwesome
import {
    faCaretDown,
    faSearch,
    faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Components
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";

// Styles
import { SelectContainer, PopUpContainer } from "./Select.styles";

type Props = {
    filterName: string;
    options: Array<Option>;
    value: string | number | null;
    setValue: Function;
    searchable?: boolean;
    disabled?: boolean;
    loadingOptions?: boolean;
    showAll?: boolean;
    label?: string;
    error?: boolean;
    errorText?: string;
    clearable?: boolean;
    fitContent?: boolean
};

export default function Select({
    filterName,
    options,
    value,
    setValue,
    searchable,
    disabled,
    showAll = true,
    loadingOptions = false,
    label,
    error = false,
    errorText,
    clearable = false,
    fitContent = false,
}: Props) {
    const [open, setOpen] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const [results, setResults] = useState<Array<Option>>(options || []);
    const [searchString, setSearchString] = useState<string>("");
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);

    const searchInputRef = useRef<HTMLInputElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (disabled) return;
        setSearchString("");
        setResults(options || []);
        setSelectedIndex(-1);
        setAnchorEl(event.currentTarget);
        setOpen(!open);

        // Auto-focus search input when dropdown opens
        if (!open && searchable) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchString(event.target.value);
        const filteredResults = options.filter((option) =>
            option.label
                .toLowerCase()
                .includes(event.target.value.toLowerCase())
        );
        setResults(filteredResults);
        setSelectedIndex(-1); // Reset selection when searching
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (!open || results.length === 0) return;

        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                setSelectedIndex((prev) =>
                    prev < results.length - 1 ? prev + 1 : 0
                );
                break;
            case "ArrowUp":
                event.preventDefault();
                setSelectedIndex((prev) =>
                    prev > 0 ? prev - 1 : results.length - 1
                );
                break;
            case "Enter":
                event.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < results.length) {
                    handleSelectFilter(results[selectedIndex]);
                }
                break;
            case "Escape":
                event.preventDefault();
                setOpen(false);
                break;
        }
    };

    const handleSelectFilter = (option: Option) => {
        setOpen(false);
        setValue(option.value);
    };

    const getValue = () => {
        const selectedOption = options.find((option) => option.value === value);

        if (selectedOption) {
            return selectedOption.label;
        } else return "Loading";
    };

    const isActive = () => {
        return value || value === 0;
    };

    useEffect(() => {
        setResults(options);
    }, [options]);

    const inputId = `select-${filterName}-search`;

    return (
        <>
            {label && <label htmlFor={inputId}>{label}</label>}
            <SelectContainer
                style={label ? { marginTop: "10px" } : {}}
                onClick={handleClick}
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        handleClick(
                            e as unknown as React.MouseEvent<HTMLElement>
                        );
                    }
                }}
                className={`${!disabled && "enabled"} ${error && "error"}`}
            >
                {isActive() ? (
                    <p className="selected">{getValue()}</p>
                ) : (
                    <>
                        <p
                            className={`filterLabel ${
                                showAll ? "" : "filterLabelFullWidth"
                            }`}
                        >
                            {filterName}
                        </p>
                        {showAll && <p className="value">ALL</p>}
                    </>
                )}
                {!disabled && clearable && isActive() && (
                    <FontAwesomeIcon
                        icon={faTimesCircle}
                        fixedWidth
                        className="clearIcon"
                        onClick={(e) => {
                            e.stopPropagation();
                            setValue("");
                        }}
                    />
                )}
                {!disabled && <FontAwesomeIcon icon={faCaretDown} fixedWidth />}
            </SelectContainer>

            {errorText && error && <p className="errorText">{errorText}</p>}

            <Popper open={open} anchorEl={anchorEl} style={{ zIndex: 1000 }}>
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                    <PopUpContainer $fitContent={fitContent}>
                        {searchable && options.length > 0 && (
                            <div className="searchBox">
                                <input
                                    id={inputId}
                                    ref={searchInputRef}
                                    placeholder="Search..."
                                    value={searchString}
                                    onChange={handleSearch}
                                    onKeyDown={handleKeyDown}
                                    autoComplete="off"
                                />
                                <FontAwesomeIcon icon={faSearch} fixedWidth />
                            </div>
                        )}
                        <div className="options">
                            {loadingOptions && (
                                <div className="loading">
                                    <LoadingSpinner />
                                </div>
                            )}

                            {results.length > 0 &&
                                results.map((option, index) => (
                                    <button
                                        type="button"
                                        className={`option ${
                                            selectedIndex === index
                                                ? "selected"
                                                : ""
                                        }`}
                                        onClick={() =>
                                            handleSelectFilter(option)
                                        }
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === "Enter" ||
                                                e.key === " "
                                            ) {
                                                handleSelectFilter(option);
                                            }
                                        }}
                                        key={`${option.value}-${option.label}`}
                                    >
                                        <p className={option?.color}>
                                            {option.label}
                                        </p>
                                    </button>
                                ))}

                            {results.length === 0 && !loadingOptions && (
                                <div className="noResults">
                                    <p>No Results</p>
                                </div>
                            )}
                        </div>
                    </PopUpContainer>
                </ClickAwayListener>
            </Popper>
        </>
    );
}
